import { ReactNode, useCallback, useState } from 'react';

import { cx } from '@libs/classnames';
import { formatName } from '@libs/formatNames';
import { CollapseIcon } from '@ui/components/CollapseIcon';
import { Fraction } from '@ui/components/Fraction';
import { GameResultsBadge } from '@ui/components/GameResultBadge';

import { GameParticipantsResult, ResultsGame } from '@features/shared/api/typings';

import styles from './BracketsMatch.module.css';

type Player = {
  userId: string;
  lastName: string;
  firstName: string;
  games: ResultsGame[];
  total: {
    score: string;
    resultType: GameParticipantsResult;
  };
  subRows?: Player[] | null;
};

export type BracketsMatchProps = {
  className?: string;
  roundName?: ReactNode;
  player1?: Player;
  player2?: Player;
  isFinal?: boolean;
  isThirdPlace?: boolean;
  onGameClick?: (gameId: string) => void;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
};

export function BracketsMatch({
  className,
  roundName,
  player1,
  player2,
  isFinal,
  isThirdPlace,
  onGameClick,
  renderShieldQuickPeek,
}: BracketsMatchProps) {
  const [open, setOpen] = useState(false);

  const toggle = useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);

  return (
    <div className={cx(styles.match, className)}>
      {roundName && <div className={styles.roundName}>{roundName}</div>}
      <BracketsMatchPlayer
        userId={player1?.userId}
        name={player1 ? formatName(player1) : 'Bye'}
        games={player1?.games || []}
        totalScore={player1?.total.score || 'Bye'}
        totalResult={player1?.total.resultType || 'Loss'}
        isFinal={isFinal}
        isThirdPlace={isThirdPlace}
        onGameClick={onGameClick}
        subRows={player1?.subRows || undefined}
        isOpen={open}
        toggle={toggle}
        renderShieldQuickPeek={renderShieldQuickPeek}
      />
      {open &&
        player1?.subRows &&
        player1.subRows.map((p, i) => (
          <BracketsMatchPlayer
            key={p.lastName + p.firstName + i}
            userId={p.userId}
            name={p ? formatName(p) : 'Bye'}
            games={p?.games || []}
            totalScore={p?.total.score || 'Bye'}
            totalResult={player1.total.resultType || 'Loss'}
            isFinal={isFinal}
            isThirdPlace={isThirdPlace}
            onGameClick={onGameClick}
            isSubRow
            skipRounds={player1.games.length - p.games.length}
            renderShieldQuickPeek={renderShieldQuickPeek}
          />
        ))}

      <BracketsMatchPlayer
        userId={player2?.userId}
        name={player2 ? formatName(player2) : 'Bye'}
        games={player2?.games || []}
        totalScore={player2?.total.score || 'Bye'}
        totalResult={player2?.total.resultType || 'Loss'}
        isFinal={isFinal}
        isThirdPlace={isThirdPlace}
        onGameClick={onGameClick}
        subRows={player2?.subRows || undefined}
        isOpen={open}
        toggle={toggle}
        renderShieldQuickPeek={renderShieldQuickPeek}
      />

      {open &&
        player2?.subRows &&
        player2.subRows.map((p, i) => (
          <BracketsMatchPlayer
            key={p.lastName + p.firstName + i}
            userId={p.userId}
            name={p ? formatName(p) : 'Bye'}
            games={p?.games || []}
            totalScore={p?.total.score || 'Bye'}
            totalResult={player2.total.resultType || 'Loss'}
            isFinal={isFinal}
            isThirdPlace={isThirdPlace}
            onGameClick={onGameClick}
            isSubRow
            skipRounds={player2.games.length - p.games.length}
            renderShieldQuickPeek={renderShieldQuickPeek}
          />
        ))}
    </div>
  );
}

type BracketsMatchPlayerProps = {
  userId?: string;
  name: string;
  games: ResultsGame[];
  totalScore: string;
  totalResult: GameParticipantsResult;
  isFinal?: boolean;
  isThirdPlace?: boolean;
  onGameClick?: (gameId: string) => void;
  subRows?: Player[];
  isOpen?: boolean;
  toggle?: () => void;
  isSubRow?: boolean;
  skipRounds?: number;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
};

function BracketsMatchPlayer({
  userId,
  name,
  games,
  totalScore,
  totalResult,
  isFinal,
  isThirdPlace,
  onGameClick,
  subRows,
  isOpen,
  toggle,
  isSubRow,
  skipRounds = 0,
  renderShieldQuickPeek,
}: BracketsMatchPlayerProps) {
  const isGroup = !!subRows;

  let winningPlace;
  if (isFinal) {
    if (totalResult === 'Win') winningPlace = 'I';
    if (totalResult === 'Loss') winningPlace = 'II';
  }
  if (isThirdPlace) {
    if (totalResult === 'Win') winningPlace = 'III';
    if (totalResult === 'Loss') winningPlace = 'IV';
  }

  const isBye = Boolean(totalResult === 'UnPlayed' && games.length === 1 && games[0]?.score);

  const isByeStub = totalScore === 'Bye';

  return (
    <div
      className={cx(styles.playerMatch, {
        [styles.loss]: totalResult === 'Loss',
        [styles.win]: totalResult === 'Win',
        [styles.final]: isFinal,
        [styles.group]: isGroup,
        [styles.subRow]: isSubRow,
      })}
    >
      <div className={styles.playerNameContainer} onClick={toggle}>
        <div className={styles.playerName}>{name}</div>
        {userId ? renderShieldQuickPeek?.(userId) : null}
      </div>

      <div className={styles.results}>
        {isGroup && (
          <CollapseIcon className={styles.collapseIcon} toggled={isOpen} onClick={toggle} />
        )}

        {!isBye &&
          games.map((g, i) => (
            <GameResultsBadge
              className={cx(styles.gameResultsBadge, {
                [styles.win]: g.resultType === 'Win',
              })}
              key={g.gameId + '' + i}
              gameId={g.gameId}
              result={g.resultType}
              score={g.score}
              side={g.side}
              dotCentered
              onGameClick={onGameClick}
            />
          ))}

        {[...Array(skipRounds)].map((_, i) => (
          <GameResultsBadge
            key={'skippedRound-' + i}
            className={styles.gameResultsBadge}
            result="UnPlayed"
            dotCentered
          />
        ))}

        <div className={styles.totalScore}>
          {totalScore && (isByeStub ? '-' : <Fraction value={totalScore} />)}
        </div>

        {winningPlace && (
          <div className={styles.winningPlaceContainer}>
            {!isSubRow && <div className={styles.winningPlace}>{winningPlace}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
